/**
 * FormBuilder Page
 * Apple-quality design with true black dark mode
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  EyeIcon,
  LinkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  PlusIcon,
  UserIcon,
  EnvelopeIcon,
  BuildingStorefrontIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import leadIntakeService from '../../services/LeadIntakeService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import QuestionEditor from '../../components/leads/QuestionEditor';
import FormStyler from '../../components/leads/FormStyler';
import AISettingsPanel from '../../components/leads/AISettingsPanel';
import { QUESTION_TYPES, FORM_TEMPLATES } from '../../components/leads/QuestionTypes';
import toast from 'react-hot-toast';

const SIGN_OFFS = [
  { id: 'warmly', label: 'Warmly', preview: 'Warmly,' },
  { id: 'best', label: 'Best', preview: 'Best,' },
  { id: 'cheers', label: 'Cheers', preview: 'Cheers,' },
  { id: 'thanks', label: 'Thanks', preview: 'Thanks,' },
  { id: 'talk_soon', label: 'Talk soon', preview: 'Talk soon,' },
  { id: 'looking_forward', label: 'Looking forward', preview: 'Looking forward to connecting,' }
];

const FormBuilder = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { formId } = useParams();
  const isNew = !formId || formId === 'new';

  const [form, setForm] = useState({
    name: '',
    description: '',
    questions: [],
    styling: {
      primaryColor: '#FFB27D',
      secondaryColor: '#000000',
      backgroundColor: '#FFFFFF',
      textColor: '#000000',
      headingFont: 'inter',
      bodyFont: 'inter',
      buttonStyle: 'rounded',
      formWidth: 'medium'
    },
    followUp: {
      enabled: true,
      delayMinutes: 15,
      emailSubject: 'Thanks for reaching out!',
      emailTemplate: 'friendly',
      customMessage: '',
      includeSignature: true
    }
  });

  const [activeTab, setActiveTab] = useState('questions');
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showSignatureSetup, setShowSignatureSetup] = useState(false);
  const [signatureForm, setSignatureForm] = useState({
    name: '',
    email: '',
    businessName: '',
    title: '',
    signOff: 'warmly'
  });
  const [savingSignature, setSavingSignature] = useState(false);
  const hasLoadedSettings = React.useRef(false);
  const hasLoadedForm = React.useRef(false);

  useEffect(() => {
    if (!isNew && currentUser?.uid && !hasLoadedForm.current) {
      hasLoadedForm.current = true;
      loadForm();
    }
    if (currentUser?.uid && !hasLoadedSettings.current) {
      hasLoadedSettings.current = true;
      loadSettings();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, currentUser?.uid, isNew]);

  const loadForm = async () => {
    try {
      // Load directly from Firestore for faster loading
      const formRef = doc(db, 'users', currentUser.uid, 'intakeForms', formId);
      const formSnap = await getDoc(formRef);

      if (!formSnap.exists()) {
        toast.error('Form not found');
        navigate('/app/leads');
        return;
      }

      setForm({ id: formSnap.id, ...formSnap.data() });
    } catch (error) {
      console.error('Failed to load form:', error);
      toast.error('Failed to load form');
      navigate('/app/leads');
    }
  };

  const loadSettings = async () => {
    try {
      // Load directly from Firestore for faster loading
      const settingsRef = doc(db, 'users', currentUser.uid, 'settings', 'leadIntake');
      const settingsSnap = await getDoc(settingsRef);

      const loadedSettings = settingsSnap.exists() ? settingsSnap.data() : {};
      setSettings(loadedSettings);

      // For new forms, check if signature is set up
      if (isNew) {
        const hasSignature = loadedSettings?.signature?.name && loadedSettings?.signature?.email;
        if (hasSignature) {
          // Signature exists, go straight to templates
          setShowTemplates(true);
        } else {
          // No signature, ask for it first
          setShowSignatureSetup(true);
        }
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
      // On error, still show signature setup for new forms
      if (isNew) {
        setShowSignatureSetup(true);
      }
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Please enter a form name');
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        const res = await leadIntakeService.createForm({
          ...form,
          questions: form.questions.map((q, i) => ({ ...q, id: q.id || `q${i + 1}` }))
        });
        toast.success('Form created');
        navigate(`/app/leads/forms/${res.form.id}`, { replace: true });
      } else {
        await leadIntakeService.updateForm(formId, currentUser.uid, form);
        toast.success('Saved');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!form.name.trim()) {
      toast.error('Please enter a form name');
      return;
    }

    if (form.questions.length === 0) {
      toast.error('Add at least one question');
      return;
    }

    setPublishing(true);
    try {
      if (isNew) {
        const res = await leadIntakeService.createForm({
          ...form,
          questions: form.questions.map((q, i) => ({ ...q, id: q.id || `q${i + 1}` }))
        });
        await leadIntakeService.publishForm(res.form.id, currentUser.uid);
        toast.success('Published');
        navigate(`/app/leads/forms/${res.form.id}`, { replace: true });
      } else {
        await leadIntakeService.updateForm(formId, currentUser.uid, form);
        const res = await leadIntakeService.publishForm(formId, currentUser.uid);
        setForm(prev => ({ ...prev, isPublished: true, slug: res.slug }));
        const url = `${window.location.origin}/intake/${res.slug}`;
        navigator.clipboard.writeText(url);
        toast.success('Published! Link copied');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  const addQuestion = (type) => {
    const questionType = QUESTION_TYPES.find(t => t.id === type);
    const newQuestion = {
      id: `q${Date.now()}`,
      type,
      label: questionType?.defaultLabel || '',
      placeholder: questionType?.defaultPlaceholder || '',
      required: false,
      aiWeight: 'medium',
      aiContext: questionType?.aiContexts?.[0] || 'general',
      options: questionType?.defaultOptions || (questionType?.hasOptions ? [{ value: 'option1', label: 'Option 1', score: 50 }] : undefined),
      scale: type === 'rating' ? 5 : undefined
    };

    setForm(prev => ({ ...prev, questions: [...prev.questions, newQuestion] }));
    setExpandedQuestion(newQuestion.id);
  };

  const applyTemplate = (template) => {
    const questionsWithIds = template.questions.map((q, i) => ({
      ...q,
      id: `q${Date.now()}_${i}`,
      options: q.options || QUESTION_TYPES.find(t => t.id === q.type)?.defaultOptions
    }));

    setForm(prev => ({
      ...prev,
      name: template.name,
      description: template.description,
      questions: questionsWithIds
    }));
    setShowTemplates(false);
  };

  const startFromScratch = () => {
    setShowTemplates(false);
  };

  const handleSaveSignature = async () => {
    if (!signatureForm.name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!signatureForm.email.trim()) {
      toast.error('Please enter your email');
      return;
    }

    setSavingSignature(true);
    try {
      const newSettings = {
        ...settings,
        signature: {
          name: signatureForm.name.trim(),
          email: signatureForm.email.trim(),
          businessName: signatureForm.businessName.trim() || signatureForm.name.trim(),
          title: signatureForm.title.trim()
        },
        aiSettings: {
          ...(settings?.aiSettings || {}),
          signOff: signatureForm.signOff
        }
      };

      await leadIntakeService.updateSettings(currentUser.uid, newSettings);
      setSettings(newSettings);
      setShowSignatureSetup(false);
      setShowTemplates(true);
      toast.success('Signature saved');
    } catch (error) {
      toast.error('Failed to save signature');
      console.error('Failed to save signature:', error);
    } finally {
      setSavingSignature(false);
    }
  };

  const updateQuestion = (id, updates) => {
    setForm(prev => ({
      ...prev,
      questions: prev.questions.map(q => q.id === id ? { ...q, ...updates } : q)
    }));
  };

  const deleteQuestion = (id) => {
    setForm(prev => ({
      ...prev,
      questions: prev.questions.filter(q => q.id !== id)
    }));
  };

  const reorderQuestions = (newOrder) => {
    setForm(prev => ({ ...prev, questions: newOrder }));
  };

  const canPublish = () => {
    const hasName = !!form.name.trim();
    const hasQuestions = form.questions.length > 0;
    const hasEmail = form.questions.some(q => q.type === 'email' || q.aiContext === 'email');
    const hasSignature = settings?.signature?.name && settings?.signature?.email;
    return hasName && hasQuestions && hasEmail && hasSignature;
  };

  const getPublishErrors = () => {
    const errors = [];
    if (!form.name.trim()) errors.push('Add a form name');
    if (form.questions.length === 0) errors.push('Add at least one question');
    if (!form.questions.some(q => q.type === 'email' || q.aiContext === 'email')) {
      errors.push('Add an email field');
    }
    return errors;
  };

  const tabs = [
    { id: 'questions', label: 'Questions' },
    { id: 'style', label: 'Style' },
    { id: 'followup', label: 'Follow-up' },
    { id: 'settings', label: 'Settings' }
  ];

  return (
    <div className="min-h-screen bg-[#F5F5F7] dark:bg-black">
      {/* Header */}
      <div className="bg-white/80 dark:bg-black/80 backdrop-blur-xl border-b border-black/5 dark:border-white/5 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between gap-2 flex-wrap sm:flex-nowrap">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
              <button
                onClick={() => navigate('/app/leads')}
                className="p-2 -ml-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors flex-shrink-0"
              >
                <ArrowLeftIcon className="w-5 h-5 text-black/50 dark:text-white/50" />
              </button>
              <div>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Untitled Form"
                  className="text-[22px] font-semibold text-black dark:text-white bg-transparent border-none
                    focus:outline-none focus:ring-0 p-0 placeholder:text-black/30 dark:placeholder:text-white/30 w-full sm:w-64"
                />
                {form.isPublished && (
                  <div className="flex items-center gap-2 mt-0.5">
                    <CheckCircleIcon className="w-3.5 h-3.5 text-green-500" />
                    <span className="text-[13px] text-green-600 dark:text-green-400">Live</span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/intake/${form.slug}`);
                        toast.success('Link copied');
                      }}
                      className="flex items-center gap-1 text-[13px] text-brand hover:underline"
                    >
                      <LinkIcon className="w-3 h-3" />
                      Copy link
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              <button
                onClick={() => setShowPreview(true)}
                className="flex items-center gap-1.5 px-2 sm:px-4 py-2 rounded-full text-[14px] font-medium
                  text-black/60 dark:text-white/60 hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
              >
                <EyeIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Preview</span>
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-3 sm:px-4 py-2 rounded-full text-[14px] font-medium
                  text-black/60 dark:text-white/60 hover:bg-black/5 dark:hover:bg-white/5
                  transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={handlePublish}
                disabled={publishing || !canPublish()}
                className="px-3 sm:px-5 py-2 bg-brand text-white rounded-full text-[14px]
                  font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {publishing ? '...' : form.isPublished ? 'Update' : 'Publish'}
              </motion.button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 sm:gap-6 mt-5 overflow-x-auto -mx-6 px-6 scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 text-[14px] font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-black dark:text-white border-black dark:border-white'
                    : 'text-black/40 dark:text-white/40 border-transparent hover:text-black/60 dark:hover:text-white/60'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Publish Requirements */}
        {!canPublish() && activeTab === 'questions' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-amber-500/10 border border-amber-500/20 rounded-2xl"
          >
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[14px] font-medium text-amber-700 dark:text-amber-400">Before publishing:</p>
                <ul className="mt-1 text-[13px] text-amber-600 dark:text-amber-400/80 space-y-0.5">
                  {getPublishErrors().map((error, i) => (
                    <li key={i}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        )}

        {/* Questions Tab */}
        {activeTab === 'questions' && (
          <div className="space-y-4">
            {/* Description */}
            <div className="bg-white dark:bg-white/5 rounded-2xl p-5 border border-black/5 dark:border-white/10">
              <label className="block text-[13px] font-medium text-black/50 dark:text-white/50 mb-2">
                Description (optional)
              </label>
              <textarea
                value={form.description || ''}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what this form is for..."
                rows={2}
                className="w-full px-4 py-3 rounded-xl bg-black/[0.03] dark:bg-white/5
                  border border-black/5 dark:border-white/10 text-black dark:text-white
                  text-[15px] placeholder:text-black/30 dark:placeholder:text-white/30
                  resize-none focus:outline-none focus:ring-2 focus:ring-brand/50 transition-shadow"
              />
            </div>

            {/* Questions List */}
            {form.questions.length > 0 && (
              <Reorder.Group values={form.questions} onReorder={reorderQuestions} className="space-y-3">
                {form.questions.map((question) => (
                  <Reorder.Item key={question.id} value={question}>
                    <QuestionEditor
                      question={question}
                      onChange={(updates) => updateQuestion(question.id, updates)}
                      onDelete={() => deleteQuestion(question.id)}
                      isOpen={expandedQuestion === question.id}
                      onToggle={() => setExpandedQuestion(expandedQuestion === question.id ? null : question.id)}
                    />
                  </Reorder.Item>
                ))}
              </Reorder.Group>
            )}

            {/* Add Question */}
            <div className="bg-white dark:bg-white/5 rounded-2xl p-5 border border-black/5 dark:border-white/10">
              <p className="text-[13px] font-medium text-black/50 dark:text-white/50 mb-3">
                Add a question
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {QUESTION_TYPES.map((type) => (
                  <motion.button
                    key={type.id}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => addQuestion(type.id)}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl
                      bg-black/[0.02] dark:bg-white/5 border border-transparent
                      hover:border-brand/50 hover:bg-brand/5 transition-all group"
                  >
                    <type.icon className="w-5 h-5 text-black/30 dark:text-white/30 group-hover:text-brand" />
                    <span className="text-[11px] font-medium text-black/40 dark:text-white/40 group-hover:text-brand">
                      {type.label}
                    </span>
                  </motion.button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Style Tab */}
        {activeTab === 'style' && (
          <div className="bg-white dark:bg-white/5 rounded-2xl p-6 border border-black/5 dark:border-white/10">
            <FormStyler
              styling={form.styling}
              onChange={(styling) => setForm(prev => ({ ...prev, styling }))}
              userId={currentUser?.uid}
            />
          </div>
        )}

        {/* Follow-up Tab - AI Nurture */}
        {activeTab === 'followup' && (
          <div className="bg-white dark:bg-white/5 rounded-2xl p-6 border border-black/5 dark:border-white/10">
            <AISettingsPanel
              settings={settings}
              onChange={async (newSettings) => {
                setSettings(newSettings);
                try {
                  await leadIntakeService.updateAISettings(currentUser.uid, newSettings);
                } catch (error) {
                  console.error('Failed to auto-save AI settings:', error);
                }
              }}
            />
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="bg-white dark:bg-white/5 rounded-2xl p-6 border border-black/5 dark:border-white/10">
            <h3 className="text-[17px] font-semibold text-black dark:text-white mb-1">Your Signature</h3>
            <p className="text-[13px] text-black/50 dark:text-white/50 mb-6">
              Required for sending follow-up emails
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[13px] font-medium text-black/50 dark:text-white/50 mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  value={settings?.signature?.name || ''}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    signature: { ...prev?.signature, name: e.target.value }
                  }))}
                  placeholder="Sarah Johnson"
                  className="w-full px-4 py-3 rounded-xl bg-black/[0.03] dark:bg-white/5
                    border border-black/5 dark:border-white/10 text-black dark:text-white text-[15px]
                    placeholder:text-black/30 dark:placeholder:text-white/30
                    focus:outline-none focus:ring-2 focus:ring-brand/50"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-black/50 dark:text-white/50 mb-2">
                  Your Email *
                </label>
                <input
                  type="email"
                  value={settings?.signature?.email || ''}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    signature: { ...prev?.signature, email: e.target.value }
                  }))}
                  placeholder="sarah@example.com"
                  className="w-full px-4 py-3 rounded-xl bg-black/[0.03] dark:bg-white/5
                    border border-black/5 dark:border-white/10 text-black dark:text-white text-[15px]
                    placeholder:text-black/30 dark:placeholder:text-white/30
                    focus:outline-none focus:ring-2 focus:ring-brand/50"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-black/50 dark:text-white/50 mb-2">
                  Business Name
                </label>
                <input
                  type="text"
                  value={settings?.signature?.businessName || ''}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    signature: { ...prev?.signature, businessName: e.target.value }
                  }))}
                  placeholder="Sarah Johnson Photography"
                  className="w-full px-4 py-3 rounded-xl bg-black/[0.03] dark:bg-white/5
                    border border-black/5 dark:border-white/10 text-black dark:text-white text-[15px]
                    placeholder:text-black/30 dark:placeholder:text-white/30
                    focus:outline-none focus:ring-2 focus:ring-brand/50"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-black/50 dark:text-white/50 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={settings?.signature?.title || ''}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    signature: { ...prev?.signature, title: e.target.value }
                  }))}
                  placeholder="Wedding Photographer"
                  className="w-full px-4 py-3 rounded-xl bg-black/[0.03] dark:bg-white/5
                    border border-black/5 dark:border-white/10 text-black dark:text-white text-[15px]
                    placeholder:text-black/30 dark:placeholder:text-white/30
                    focus:outline-none focus:ring-2 focus:ring-brand/50"
                />
              </div>
            </div>

            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={async () => {
                try {
                  await leadIntakeService.updateSettings(currentUser.uid, settings);
                  toast.success('Settings saved');
                } catch (error) {
                  toast.error('Failed to save');
                }
              }}
              className="mt-6 px-5 py-2.5 bg-brand text-white rounded-full text-[14px] font-semibold"
            >
              Save Settings
            </motion.button>
          </div>
        )}
      </div>

      {/* Signature Setup Modal - Shows first for new forms without signature */}
      <AnimatePresence>
        {showSignatureSetup && isNew && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative bg-white dark:bg-black rounded-3xl max-w-xl w-full max-h-[90vh] overflow-auto shadow-2xl
                border border-transparent dark:border-white/10"
            >
              <div className="p-8">
                {/* Header */}
                <div className="flex items-center gap-4 mb-2">
                  <div className="w-12 h-12 rounded-2xl bg-brand/10 flex items-center justify-center">
                    <SparklesIcon className="w-6 h-6 text-brand" />
                  </div>
                  <div>
                    <h2 className="text-[24px] font-semibold text-black dark:text-white tracking-tight">
                      Let's set you up
                    </h2>
                    <p className="text-[14px] text-black/50 dark:text-white/50">
                      How should we sign your emails?
                    </p>
                  </div>
                </div>

                <p className="text-[14px] text-black/60 dark:text-white/60 mt-4 mb-8">
                  Your leads will receive personalized follow-up emails. Set up your signature so every email looks professional.
                </p>

                {/* Signature Form */}
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="flex items-center gap-2 text-[13px] font-medium text-black/60 dark:text-white/60 mb-2">
                        <UserIcon className="w-4 h-4" />
                        Your Name *
                      </label>
                      <input
                        type="text"
                        value={signatureForm.name}
                        onChange={(e) => setSignatureForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Sarah Johnson"
                        className="w-full px-4 py-3 rounded-xl bg-black/[0.03] dark:bg-white/5
                          border border-black/5 dark:border-white/10 text-black dark:text-white text-[15px]
                          placeholder:text-black/30 dark:placeholder:text-white/30
                          focus:outline-none focus:ring-2 focus:ring-brand/50"
                      />
                    </div>
                    <div>
                      <label className="flex items-center gap-2 text-[13px] font-medium text-black/60 dark:text-white/60 mb-2">
                        <EnvelopeIcon className="w-4 h-4" />
                        Your Email *
                      </label>
                      <input
                        type="email"
                        value={signatureForm.email}
                        onChange={(e) => setSignatureForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="sarah@example.com"
                        className="w-full px-4 py-3 rounded-xl bg-black/[0.03] dark:bg-white/5
                          border border-black/5 dark:border-white/10 text-black dark:text-white text-[15px]
                          placeholder:text-black/30 dark:placeholder:text-white/30
                          focus:outline-none focus:ring-2 focus:ring-brand/50"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="flex items-center gap-2 text-[13px] font-medium text-black/60 dark:text-white/60 mb-2">
                        <BuildingStorefrontIcon className="w-4 h-4" />
                        Business Name
                      </label>
                      <input
                        type="text"
                        value={signatureForm.businessName}
                        onChange={(e) => setSignatureForm(prev => ({ ...prev, businessName: e.target.value }))}
                        placeholder="Sarah Johnson Photography"
                        className="w-full px-4 py-3 rounded-xl bg-black/[0.03] dark:bg-white/5
                          border border-black/5 dark:border-white/10 text-black dark:text-white text-[15px]
                          placeholder:text-black/30 dark:placeholder:text-white/30
                          focus:outline-none focus:ring-2 focus:ring-brand/50"
                      />
                    </div>
                    <div>
                      <label className="flex items-center gap-2 text-[13px] font-medium text-black/60 dark:text-white/60 mb-2">
                        Title
                      </label>
                      <input
                        type="text"
                        value={signatureForm.title}
                        onChange={(e) => setSignatureForm(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Wedding Photographer"
                        className="w-full px-4 py-3 rounded-xl bg-black/[0.03] dark:bg-white/5
                          border border-black/5 dark:border-white/10 text-black dark:text-white text-[15px]
                          placeholder:text-black/30 dark:placeholder:text-white/30
                          focus:outline-none focus:ring-2 focus:ring-brand/50"
                      />
                    </div>
                  </div>

                  {/* Sign-off selector */}
                  <div>
                    <label className="block text-[13px] font-medium text-black/60 dark:text-white/60 mb-3">
                      How do you like to sign off?
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {SIGN_OFFS.map((signOff) => (
                        <button
                          key={signOff.id}
                          onClick={() => setSignatureForm(prev => ({ ...prev, signOff: signOff.id }))}
                          className={`px-4 py-3 rounded-xl text-[14px] font-medium transition-all ${
                            signatureForm.signOff === signOff.id
                              ? 'bg-brand text-white'
                              : 'bg-black/[0.03] dark:bg-white/5 text-black/60 dark:text-white/60 hover:bg-black/[0.06] dark:hover:bg-white/10'
                          }`}
                        >
                          {signOff.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Preview */}
                  {signatureForm.name && (
                    <div className="mt-6 p-4 rounded-xl bg-black/[0.02] dark:bg-white/[0.02] border border-black/5 dark:border-white/5">
                      <p className="text-[12px] font-medium text-black/40 dark:text-white/40 mb-2">SIGNATURE PREVIEW</p>
                      <div className="text-[14px] text-black dark:text-white whitespace-pre-line">
                        {SIGN_OFFS.find(s => s.id === signatureForm.signOff)?.preview || 'Warmly,'}
                        {'\n'}{signatureForm.name || 'Your Name'}
                        {signatureForm.title && `\n${signatureForm.title}`}
                        {signatureForm.businessName && signatureForm.businessName !== signatureForm.name && `\n${signatureForm.businessName}`}
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-3 mt-8">
                  <button
                    onClick={() => navigate('/app/leads')}
                    className="flex-1 py-3.5 rounded-full text-[14px] font-medium
                      text-black/60 dark:text-white/60 hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                  >
                    Cancel
                  </button>
                  <motion.button
                    whileTap={{ scale: 0.97 }}
                    onClick={handleSaveSignature}
                    disabled={savingSignature || !signatureForm.name || !signatureForm.email}
                    className="flex-1 py-3.5 bg-brand text-white rounded-full text-[14px]
                      font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {savingSignature ? 'Saving...' : 'Continue'}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Template Selector Modal */}
      <AnimatePresence>
        {showTemplates && isNew && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative bg-white dark:bg-black rounded-3xl max-w-2xl w-full max-h-[85vh] overflow-auto shadow-2xl
                border border-transparent dark:border-white/10"
            >
              <div className="p-8">
                <h2 className="text-[28px] font-semibold text-black dark:text-white tracking-tight mb-2">
                  Create New Form
                </h2>
                <p className="text-[15px] text-black/50 dark:text-white/50 mb-8">
                  Start with a template or build from scratch
                </p>

                {/* Templates Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  {FORM_TEMPLATES.map((template) => (
                    <motion.button
                      key={template.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => applyTemplate(template)}
                      className="text-left p-5 rounded-2xl border border-black/5 dark:border-white/10
                        bg-black/[0.02] dark:bg-white/5 hover:border-brand/50 hover:bg-brand/5
                        transition-all group"
                    >
                      <div className="flex-1 min-w-0">
                        <h3 className="text-[15px] font-semibold text-black dark:text-white group-hover:text-brand">
                          {template.name}
                        </h3>
                        <p className="text-[13px] text-black/50 dark:text-white/50 mt-1">
                          {template.description}
                        </p>
                        <p className="text-[12px] text-black/30 dark:text-white/30 mt-2">
                          {template.questions.length} questions
                        </p>
                      </div>
                    </motion.button>
                  ))}
                </div>

                {/* Divider */}
                <div className="relative my-8">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-black/10 dark:border-white/10" />
                  </div>
                  <div className="relative flex justify-center text-[13px]">
                    <span className="px-4 bg-white dark:bg-black text-black/40 dark:text-white/40">
                      or
                    </span>
                  </div>
                </div>

                {/* Start from scratch */}
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={startFromScratch}
                  className="w-full p-5 rounded-2xl border-2 border-dashed border-black/10 dark:border-white/10
                    hover:border-brand/50 hover:bg-brand/5 transition-all group"
                >
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-black/5 dark:bg-white/5 flex items-center justify-center
                      group-hover:bg-brand/20">
                      <PlusIcon className="w-5 h-5 text-black/40 dark:text-white/40 group-hover:text-brand" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-[15px] font-semibold text-black dark:text-white group-hover:text-brand">
                        Start from scratch
                      </h3>
                      <p className="text-[13px] text-black/50 dark:text-white/50">
                        Build your own custom form
                      </p>
                    </div>
                  </div>
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preview Modal */}
      <AnimatePresence>
        {showPreview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowPreview(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative bg-white rounded-3xl max-w-lg w-full max-h-[85vh] overflow-auto shadow-2xl"
              style={{
                backgroundColor: form.styling?.backgroundColor || '#FFFFFF',
                color: form.styling?.textColor || '#000000'
              }}
            >
              <button
                onClick={() => setShowPreview(false)}
                className="absolute top-4 right-4 p-2 rounded-full bg-black/5 hover:bg-black/10 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>

              <div className="p-8">
                {form.styling?.headerImage && (
                  <img
                    src={form.styling.headerImage}
                    alt=""
                    className="w-full h-40 object-cover rounded-2xl mb-6"
                  />
                )}
                {form.styling?.logo && (
                  <img src={form.styling.logo} alt="Logo" className="h-10 mb-6" />
                )}
                <h2 className="text-[28px] font-semibold tracking-tight mb-2">
                  {form.name || 'Untitled Form'}
                </h2>
                {form.description && (
                  <p className="text-[15px] opacity-60 mb-8">{form.description}</p>
                )}

                <div className="space-y-5">
                  {form.questions.map((q) => (
                    <div key={q.id}>
                      <label className="block text-[15px] font-medium mb-2">
                        {q.label || 'Question'} {q.required && <span className="text-red-500">*</span>}
                      </label>
                      {q.type === 'textarea' ? (
                        <textarea
                          placeholder={q.placeholder}
                          rows={3}
                          className="w-full px-4 py-3 rounded-xl border border-black/10 bg-black/[0.02]"
                          disabled
                        />
                      ) : q.type === 'select' || q.type === 'budget' ? (
                        <select className="w-full px-4 py-3 rounded-xl border border-black/10 bg-black/[0.02]" disabled>
                          <option>{q.placeholder || 'Select...'}</option>
                          {q.options?.map((opt) => (
                            <option key={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type={q.type === 'email' ? 'email' : q.type === 'date' ? 'date' : 'text'}
                          placeholder={q.placeholder}
                          className="w-full px-4 py-3 rounded-xl border border-black/10 bg-black/[0.02]"
                          disabled
                        />
                      )}
                    </div>
                  ))}
                </div>

                <button
                  className={`w-full mt-8 py-4 font-semibold text-[15px] ${
                    form.styling?.buttonStyle === 'pill' ? 'rounded-full' :
                    form.styling?.buttonStyle === 'square' ? 'rounded-none' : 'rounded-xl'
                  }`}
                  style={{
                    backgroundColor: form.styling?.primaryColor || '#FFB27D',
                    color: '#000000'
                  }}
                  disabled
                >
                  Submit
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FormBuilder;
